import { PaymentMethod } from '../payment-types';
import { OrderSource } from '../models/OrderSource';
import { Order } from '../models/Order';

import { withTokensIfNeeded } from 'common/api/utils/withTokensIfNeeded';
import { SECURED_E_COMM_URL, ORDERS_URL } from 'common/api/config';
import { toQueryString } from 'common/utils/queryString';
import { ThreeDSResponse, ThreeDSTransactionError } from 'common/modules/3dsecure';
import { Address } from 'common/api/models/Address';
import { FamilyAddress } from 'common/api/crm/family/address/models/FamilyAddress';

export enum TokenType {
    Unknown = 'Unknown',
    NewSingleUse = 'NewSingleUse',
    NewRecurring = 'NewRecurring',
    Saved = 'Saved',
}

export interface PaymentProviderResponse {
    cardToken: string | null;
    convergeResponse: string | null;
    expectedChargeAmount: number;
    transactionToken3DSecure: string | null;
    billingAddress: FamilyAddress | Address | { zipCode: string } | null;
    tokenType: TokenType;
}

export interface PlaceOrderRequest {
    blackBoxValue: string;
    secondaryBlackBoxValue?: string;
    cartId: string;
    expectedEarnings: number;
    paymentMethod: PaymentMethod;
    orderSource: OrderSource;
    paymentProviderResponse: PaymentProviderResponse | null;
    payment3DSecureResponse: ThreeDSTransactionError | ThreeDSResponse | null;
    clientVersion: string | null;
    firstName: string;
    lastName: string;
    email: string;
    savePaymentDetails?: boolean;
}

export enum PlaceOrderResult {
    Success = 'Success',
    EarningsMismatch = 'EarningsMismatch',
    Hold = 'Hold',
    ExceedsFinCenLimit = 'ExceedsFinCenLimit',
    ContainsUnavailableItems = 'ContainsUnavailableItems',
    CreditCardPaymentRejected = 'CreditCardPaymentRejected',
}

export interface PlaceOrderResponse {
    result: PlaceOrderResult;
    newCreditCardId: number | null;
    orderId: number | null;
    actualEarnings: number | null;
    errorMessage: string | null;
    errorCode: string | null;
}

export const placeOrder = (order: PlaceOrderRequest, apiVersion = '1.1') =>
    withTokensIfNeeded<PlaceOrderResponse, PlaceOrderResponse>(
        `${ORDERS_URL}?api-version=${apiVersion}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(order),
        }
    );

export interface Item {
    productId: number;
    quantity: number;
    priceOverride: number | null;
    selectedOptions: { [optionId: number]: string };
    cardId?: string;
}

export interface BuyNowOrderRequest
    extends Omit<
        PlaceOrderRequest,
        'cartId' | 'firstName' | 'lastName' | 'email' | 'expectedEarnings'
    > {
    cartId?: string; // Make cartId optional, not required for normal buy-now flow
    item: Item;
}

export interface BuyNowOrderResponse {
    earnings: null | number;
    errorCode: null | string;
    errorMessage: null | string;
    orderId: null | number;
}

export const buyNowPlaceOrder = (order: BuyNowOrderRequest) => {
    return withTokensIfNeeded<BuyNowOrderResponse>(`${ORDERS_URL}/buy-now`.toLowerCase(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(order),
    });
};

export const getOrders = (startDate: string, endDate?: string) =>
    withTokensIfNeeded<Order[]>(`${ORDERS_URL}?${toQueryString({ startDate, endDate })}`);

export const getOrdersV2 = (startDate: string, endDate?: string) =>
    withTokensIfNeeded<Order[]>(
        `${ORDERS_URL}?api-version=2&${toQueryString({ StartDate: startDate, EndDate: endDate })}`
    );

export interface AddBillingInfoRequest {
    cartId: string;
    firstName: string;
    lastName: string;
    email: string;
    paymentMethod: PaymentMethod;
}

export const addBillingInfo = (billingInfo: AddBillingInfoRequest) =>
    withTokensIfNeeded<null>(`${ORDERS_URL}/billing`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(billingInfo),
    });

interface ShippingSelection {
    consignmentId: string;
    shippingOptionId: string;
}
export interface SetShippingOptionsRequest {
    cartId: string;
    shipToHomeSelection: ShippingSelection | null;
    shipToCoordinatorSelection: ShippingSelection | null;
    paymentMethod: PaymentMethod;
}
export const setShippingOptions = (shippingOptions: SetShippingOptionsRequest) =>
    withTokensIfNeeded(`${ORDERS_URL}/shipping`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(shippingOptions),
    });

export interface ShippingMethodsRequest {
    cartId: string;
    customerLastName: string;
    customerEmail: string;
    customerFirstName?: string;
    shipToHomeLineItemIds?: string[];
    shippingAddress?: {
        address1: string;
        address2?: string;
        city: string;
        state: string;
        zipCode: string;
        firstName: string;
        lastName: string;
        phoneNumber: string | null;
        email: string;
    };
}
export interface ShippingOption {
    id: string;
    name: string;
    cost: number;
}

export interface ShippingMethodsResponse {
    shipToHomeConsignment: {
        id: string;
        shippingOptions: ShippingOption[];
    };
    shipToCoordinatorConsignment: {
        id: string;
        shippingOptions: ShippingOption[];
    };
}

export const getShippingMethods = (request: ShippingMethodsRequest) => {
    const sanitizedRequest: ShippingMethodsRequest = {
        ...request,
        shippingAddress: request.shippingAddress && {
            ...request.shippingAddress,
            phoneNumber: null,
        },
    };
    return withTokensIfNeeded<ShippingMethodsResponse, ShippingMethodsResponse>(
        `${ORDERS_URL}/shipping?api-version=1`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(sanitizedRequest),
        }
    );
};

export const cloDidNotLink = (errorCode: PlaceOrderResponse['errorCode']) => {
    if (!errorCode) {
        return;
    }
    const cloErrorCodes = [
        'ecomm-0042',
        'ecomm-0043',
        'ecomm-0044',
        'ecomm-0045',
        'ecomm-0046',
        'ecomm-0047',
        'ecomm-0048',
        'ecomm-0049',
        'ecomm-0050',
        'ecomm-0051',
        'ecomm-0052',
        'ecomm-0053',
        'ecomm-0054',
        'ecomm-0055',
        'ecomm-0056',
        'ecomm-0057',
    ];
    return cloErrorCodes.includes(errorCode);
};

// Travel Order History
export type TravelHistoryRecord = {
    id: string;
    benefitAmount: number;
    raiseRightBenefitAmount: number;
    organizationBenefitAmount: number;
    createDate: string;
    updateDate: string;
    travelBookingType: string;
    travelOrganizationName: string;
    travelOrganizationId: string;
    programName: string;
    programId: string;
    transactionId: string; // use this for `id`
    purchaseDate: string;
    processDate: string;
    transactionDateTime: string;
    userId: string;
    memberFirstName: string;
    memberLastName: string;
    memberEmail: string;
    product: string;
    reservationDetailName: string;
    // NOTE: Use these 2 for trip start date & trip end date
    startDate: string;
    endDate: string;
    guestName: string;
    guestEmail: string;
    cancelledDate: string | null;
    reservationStreetAddress: string;
    reservationCity: string;
    reservationStateRegion: string;
    reservationCountry: string;
    reservationPostalCode: string;
    reservationType: string;
    reservationNumberOfNights: number | null;
    reservationNumberOfAdults: number;
    reservationNumberOfChildren: number | null;
    reservationNumberOfUnits: number;
    totalPrice: number;
    taxesAndFees: number;
    netPrice: number;
    totalPublicPrice: number;
    estimatedMemberSavings: number;
    alternateCurrencyMemberReward: number;
    alternateCurrencyName: string | null;
    alternateCurrencyPerUsd: number;
    cashbackStatementCredit: number;
    paymentCardType: string;
    cardLastFour: string; // Should we just drop this?
    adjustmentType: string | null;
    adjustmentSubType: string | null;
};

export type TravelOrderHistoryResponse = {
    booked: TravelHistoryRecord[];
    completed: TravelHistoryRecord[];
    cancelled: TravelHistoryRecord[];
    errorCode?: string;
    errorMessage?: string;
};

export const getTravelOrderHistory = () => {
    return withTokensIfNeeded<TravelOrderHistoryResponse, TravelOrderHistoryResponse>(
        `${SECURED_E_COMM_URL}/travel/histories`,
        {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        }
    );
};
